import React from 'react'

import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import Flex from 'styled-flex-component'

import ActionIcon from '@components/UI/ActionIcon'
import StopPropagation from '@components/UI/StopPropagation/StopPropagation'
import { useModal } from '@context/Modal'
import { EventTrackingNames } from '@enums'
import { IApplicationState } from '@store'
import {
  selectBrandFanGroupDeleteLink,
  selectBrandFanGroupHasLiveCampaigns,
} from '@store/brandFans'
import Theme from '@theme'
import { Container, DropdownMenu, Icon, MenuItem } from '@tribegroup'
import {
  BrandFansGroupModalDelete,
  BrandFansGroupModalEditDescription,
  BrandFansGroupModalRename,
} from '../Modal'
import BrandFansGroupRowCreateCampaignLink from './CreateCampaign/Link'
import BrandFansGroupRowTrackedRoute from './TrackedRoute'

interface IProps {
  groupId: number
  canCreateCampaign: boolean
  hideCreateCampaign?: boolean
}

const BrandFansGroupRowActions: React.FC<IProps> = ({
  groupId,
  canCreateCampaign,
  hideCreateCampaign = false,
}) => {
  const { showModal } = useModal()
  const hasLiveCampaigns = useSelector((state: IApplicationState) =>
    selectBrandFanGroupHasLiveCampaigns(state, groupId),
  )
  const hasDeleteLink = useSelector((state: IApplicationState) =>
    Boolean(selectBrandFanGroupDeleteLink(state, groupId)),
  )
  const openRenameModal = () => {
    showModal(BrandFansGroupModalRename, { groupId })
  }
  const openEditDescriptionModal = () => {
    showModal(BrandFansGroupModalEditDescription, { groupId })
  }
  const openDeleteModal = () => {
    showModal(BrandFansGroupModalDelete, { groupId })
  }

  const canDelete = !hasLiveCampaigns && hasDeleteLink

  return (
    <Flex alignCenter>
      <Container rightOuterSpacing={0.5}>
        <StopPropagation>
          <DropdownMenu
            indicatorSpace={0}
            top={0}
            bottom="auto"
            right={-1.25}
            width={11.75}
            elementName="brand-fans-group-actions-menu"
            target={
              <ActionIcon
                hoverBackgroundColor={Theme.grey300Color}
                size={1}
                glyph="more"
                color={Theme.defaultColor}
                elementName="brand-fans-group-actions"
              />
            }
          >
            {!hideCreateCampaign && (
              <BrandFansGroupRowCreateCampaignLink
                eventName={EventTrackingNames.BrandFansGroupsListMenu}
                groupId={groupId}
                canCreateCampaign={canCreateCampaign}
                additionalProperties={{ group_action: 'start_campaign' }}
              >
                <MenuItem>
                  <FormattedMessage id="brandFans.groups.actions.createCampaign" />
                </MenuItem>
              </BrandFansGroupRowCreateCampaignLink>
            )}

            <BrandFansGroupRowTrackedRoute
              eventName={EventTrackingNames.BrandFansGroupsListMenu}
              groupId={groupId}
              hasTracking
              additionalProperties={{ group_action: 'rename' }}
            >
              <MenuItem onClick={openRenameModal}>
                <FormattedMessage id="brandFans.groups.actions.renameGroup" />
              </MenuItem>
            </BrandFansGroupRowTrackedRoute>
            <BrandFansGroupRowTrackedRoute
              eventName={EventTrackingNames.BrandFansGroupsListMenu}
              groupId={groupId}
              hasTracking
              additionalProperties={{ group_action: 'edit_description' }}
            >
              <MenuItem onClick={openEditDescriptionModal}>
                <FormattedMessage id="brandFans.groups.actions.editDescription" />
              </MenuItem>
            </BrandFansGroupRowTrackedRoute>
            {canDelete && (
              <BrandFansGroupRowTrackedRoute
                eventName={EventTrackingNames.BrandFansGroupsListMenu}
                groupId={groupId}
                hasTracking
                additionalProperties={{ group_action: 'delete' }}
              >
                <MenuItem onClick={openDeleteModal} data-testid="brand-fans-group-delete-menu-item">
                  <FormattedMessage id="brandFans.groups.actions.deleteGroup" />
                </MenuItem>
              </BrandFansGroupRowTrackedRoute>
            )}
          </DropdownMenu>
        </StopPropagation>
      </Container>
      <Icon size={1.5} glyph="chevron-right" color={Theme.defaultColor} />
    </Flex>
  )
}

export { BrandFansGroupRowActions }
export default BrandFansGroupRowActions
